
/**
 * ? since  31.01.2020
 * ? author Benjamin Kaul
 *
 * * Post Table Component
 *
 * Shows post queried from given endpoints of the Wordpress REST api.
 * Creates pagination and filter functionality. Needs a resource descriptor
 * with at least and endpoint url for data supply.
 *
 * @param postsPerPage How many posts should be displayed on any page, defaults to 4
 *
 * @param showHeading  Wether to show an h3 above the table, defaults to false
 *
 * @param endpointUrl  Wordpress REST api data endpoint for post supply
 *
 * @param title        h3 heading content
 *
 * @param singleTitle  Optional: Label for single post item
 *
 * @param pluralTitle  Optional: Label for multiple post items
 *
 * @param linkLabel    Optional: Label for permalink anchor text
 *
 * @param yearSelect   Label for year select
 *
 */
const PostTable = Vue.component('post-table', {

    props: {
        /**
         * Amount of posts to show per page, must be conform with api endpoint
         */
        'postsPerPage': {
            type: Number,
            default: 4
        },

        /**
         * Wether to show a heading or not
         */
        'showHeading': {
            type: Boolean,
            default: false
        },

        /**
         * Language for strings
         */
        'language': {
            type: String,
            default: 'de'
        },

        /**
         * Wordpress REST api endpoint url and strings for it's labels
         */
        'resourceDescriptor': {
            'endpointUrl': {
                type: String,
                required: true
            },
            'title': {
                type: String,
                default: 'Alle Beiträge'
            },
            'singleTitle': {
                type: String,
                default: 'Ein Beitrag'
            },
            'pluralTitle': {
                type: String,
                default: 'Beiträge aus den Bereichen...'
            },
            'noAvaialble': {
                type: String,
                default: 'Keine Beiträge verfügbar'
            },
            'linkLabel': {
                type: String,
                default: 'Zum Beitrag'
            },
            'yearSelect': {
                type: String,
                default: 'Jahr auswählen'
            }
        }
    },

    template: `
        <div class="o-container">
            <h3 v-if="showHeading">{{ resourceDescriptor.title }}</h3>
            <h6>{{ referenceCount }}</h6>
            <div class="c-references-filter">
                <select class="c-references-year-select" name="year" id="year" v-model="year" v-on:change="resetCurrentPage">
                    <option value="" disabled>{{ resourceDescriptor.yearSelect }}</option>
                    <option v-bind:value="year" v-for="year in yearsAvailable">{{ year }}</option>
                </select>
                <span v-for="(name, id) in tagsAvailable" v-bind:key="id" v-on:click="toggleFilter(id)" class="c-references-filter-item" v-bind:class="filter.indexOf(Number(id)) !== -1 ? 'c-references-filter-item-active' : ''">{{ name }}</span>
            </div>
            <table class="c-references" v-if="paginatedPosts.length > 0">
                <tbody is="transition-group" name="ranim" appear>
                    <tr is="post-table-item" v-for="post in paginatedPosts" v-bind:post="post" v-bind:key="post.id" v-bind:type="resourceDescriptor.linkLabel"></tr>
                </tbody>
            </table>
            <p v-else>{{ resourceDescriptor.noAvailable }}</p>
            <div class="c-pagination">
                <button class="c-pagination-item" v-for="index in filteredAvailablePages" v-bind:key="index" v-bind:class="(Number(index) - 1) === currentPage ? 'c-pagination-item-active' : ''" v-on:click.prevent="paginate(index)">{{ index }}</button>
            </div>
        </div>`,

    data: function() {
        return {
            /**
             * All posts
             */
            posts: [],

            /**
             * All active filters
             */
            filter: [],

            /**
             * Active year filter
             */
            year: "",

            /**
             * Total amount of pages available
             */
            totalAvailablePages: null,

            /**
             * Active page
             */
            currentPage: 0
        }
    },

    computed: {

        /**
         * Dynamic heading for the amount of posts
         */
        referenceCount: function() {
            return this.filteredPosts.length === 1 ?
            this.resourceDescriptor.singleTitle :
            this.resourceDescriptor.pluralTitle;
        },

        /**
         * Different tags available in all posts
         */
        tagsAvailable: function() {
            let results = Object.create(null);

            this.posts.forEach(function(post) {
                post.categories.forEach(function(cat) {
                    /**
                     * Overwrite, to push each category only once
                     */
                    results[cat.term_id] = cat.name;
                });
            });

            return results;
        },

        /**
         * Different years available in all posts
         */
        yearsAvailable: function() {
            let years = [];

            this.posts.forEach(function(post) {
                let date = new Date(post.date).getFullYear();

                /**
                 * Push each year only once
                 */
                if (years.indexOf(date) === -1) {
                    years.push(date);
                }
            });

            return years;
        },

        /**
         * Posts filtered by active filter
         */
        filteredPosts: function(number) {

            /**
             * Get all posts by default
             */
            let vm = this, filteredPosts = this.posts;

            /**
             * Are there active category filters?
             */
            if (this.filter.length > 0) {
                filteredPosts = filteredPosts.filter(function(post) {

                    /**
                     * Get the id for every category of the post
                     */
                    let postCatIds = post.categories.map(function(cat) {
                        return cat.term_id;
                    });

                    /**
                     * Loop filters
                     */
                    for (let filterIndex = 0; filterIndex < vm.filter.length; filterIndex++) {
                        let f = vm.filter[filterIndex];
                        /**
                         * Is the id of the filter found within the categories of the post?
                         */
                        if (postCatIds.indexOf(f) !== -1) {
                            return true;
                        }
                    }

                    return false;
                });
            }

            /**
             * Is there a year filter?
             */
            if (this.year !== "") {
                /**
                 * Loop posts
                 */
                filteredPosts = filteredPosts.filter(function(post) {
                    let date = new Date(post.date).getFullYear();
                    /**
                     * Does the post has this year?
                     */
                    return date === vm.year;
                });
            }

            return filteredPosts;
        },

        /**
         * How many pages of posts are available for the current filter settings
         */
        filteredAvailablePages: function() {
            return Math.ceil(this.filteredPosts.length / this.postsPerPage);
        },

        /**
         * All posts for the current page
         */
        paginatedPosts: function() {
            let offset = this.currentPage * this.postsPerPage;
            let end = offset + this.postsPerPage;
            let paged = this.filteredPosts.slice(offset, end);

            return paged;
        }
    },

    methods: {

        /**
         * Adds or removes filter
         */
        toggleFilter: function(tagId) {

            let id = Number(tagId);

            if (this.filter.includes(id)) {
                this.filter = this.filter.filter(function(el) {
                    return el !== id;
                });
            }
            else {
                this.filter.push(id);
            }

            this.currentPage = 0;
        },

        /**
         * Set current page to 0
         */
        resetCurrentPage: function() {
            this.currentPage = 0;
        },

        /**
         * Fetch page of posts for the given page index
         */
        fetchPage: function(pageIndex) {

            /**
             * If all pages are fetched, return
             */
            if (this.totalAvailablePages && pageIndex > this.totalAvailablePages) {
                return;
            }

            let vm = this;

            fetch(vm.resourceDescriptor.endpointUrl + pageIndex)

            .then(function(response) {
                if (response.status !== 200) {
                    let err = new Error('no posts available');
                    err.status = response.status;
                    throw err;
                }
                vm.totalAvailablePages = Number(response.headers.get('WP-TotalPages'));
                return response.json();
            })

            .then(function(data) {

                if (!data) {
                    let err = new Error('no posts available');
                    err.status = 404;
                    throw err;
                }

                vm.posts = vm.posts.concat(data);
                vm.fetchPage(pageIndex + 1);
            })

            .catch(function(err) {
                console.log(`Failed to found posts, server reports ${err.status}`);
                console.log('Server:', err.message);
            });
        },

        /**
         * Move forward to the given page
         */
        paginate: function(pageIndex) {
            this.currentPage =  Number(pageIndex) - 1;
        }
    },

    created: function() {
        /**
         * Start off by fetching the first page of posts
         */
        this.fetchPage(1);
    }
});

export default PostTable;